import mqtt from '@/utils/mqtt'
import { getDeliveryTask, getStandaloneRoute, dispatchDeliveryCommand } from '@/utils/api/table'

const resetInFlight = new Set()

function requestId () {
  const bytes = new Uint8Array(16)
  window.crypto.getRandomValues(bytes)
  return Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('')
}

// Listen for the matching live reply; cached telemetry cannot complete a handshake.
export function requestMissionReply (plane, command, field, payload = {}, dispatch = null) {
  return new Promise((resolve, reject) => {
    if (!plane || !mqtt.isConnected()) return reject(new Error('指令服务器未连接'))
    const client = mqtt.client
    if (!client || !client.connected) return reject(new Error('指令服务器未连接'))
    const id = requestId()
    const pending = { timer: null, dispose: null, settled: false, dispatched: !dispatch, reply: null }
    const finish = (error, reply) => {
      if (pending.settled) return
      pending.settled = true
      clearTimeout(pending.timer)
      if (pending.dispose) pending.dispose()
      if (error) reject(error)
      else resolve(reply)
    }
    const topic = `planeState/${plane.shop_id}/${plane.plane_code}`
    // Reuse terminalRuntime's wildcard subscription; never unsubscribe another request.
    const onMessage = (messageTopic, message) => {
      if (messageTopic !== topic) return
      try {
        const data = JSON.parse(message.toString())
        const reply = typeof data[field] === 'string' ? JSON.parse(data[field]) : data[field]
        if (reply && reply.requestId === id) {
          pending.reply = reply
          if (pending.dispatched) finish(null, reply)
        }
      } catch (error) { /* Ignore unrelated or malformed telemetry. */ }
    }
    const onClose = () => finish(new Error('任务核对期间连接中断，未确认执行'))
    client.on('message', onMessage)
    client.on('close', onClose)
    pending.dispose = () => {
      client.removeListener('message', onMessage)
      client.removeListener('close', onClose)
    }
    pending.timer = setTimeout(() => finish(new Error('飞机任务确认超时，请检查连接及固件版本')), 8000)
    if (dispatch) {
      Promise.resolve().then(() => {
        if (pending.settled) throw new Error('任务核对已结束')
        return dispatch({ ...payload, requestId: id })
      }).then(() => {
        pending.dispatched = true
        if (pending.reply) finish(null, pending.reply)
      }, error => finish(error))
    } else if (!mqtt.publishCommand(plane, JSON.stringify({ [command]: { ...payload, requestId: id } }))) {
      finish(new Error('任务确认请求发送失败'))
    }
  })
}

// The backend checks the locked aircraft binding, not the filtered UI order list.
export async function autoResetOrphanMission (plane, store) {
  const state = plane?.planeState
  if (!state?.online || !state.espOnline || state.isUnlock !== false || plane.mission_route_id ||
      Number(state.questState) === 1 || state.orphanResetPhase !== 'waiting' ||
      Date.now() - state.lastFlightHeartbeatAt > 5000 || resetInFlight.has(`${plane.shop_id}/${plane.id}`)) return
  state.orphanResetPhase = 'trying'
  try {
    await resetOrphanMissionManually(plane, store)
    if (state.orphanResetPhase === 'trying') state.orphanResetPhase = 'confirmed'
  } catch (error) {
    if (state.orphanResetPhase === 'trying') state.orphanResetPhase = state.online ? 'failed' : 'waiting'
  }
}
export async function resetOrphanMissionManually (plane, store) {
  const state = plane?.planeState
  if (!state?.online || !state.espOnline || state.isUnlock !== false ||
      Date.now() - state.lastFlightHeartbeatAt > 5000) throw new Error('请等待飞机和飞控在线并加锁')
  const key = `${plane.shop_id}/${plane.id}`
  if (resetInFlight.has(key)) throw new Error('飞机正在复位，请稍后重试')
  resetInFlight.add(key)
  try {
    const status = await requestMissionReply(plane, 'missionStatusRequest', 'missionHandshake')
    if (status.armed || !state.online || !state.espOnline || state.isUnlock !== false ||
        Date.now() - state.lastFlightHeartbeatAt > 5000) throw new Error('飞机状态已变化，请确认加锁后重试')
    if (state.missionToken && state.missionToken !== status.missionToken) throw new Error('飞机任务标识已变化，请等待状态同步')
    if (Number(status.questState) === 1) return
    // A missing identity needs a nonempty command marker; backend binding and firmware
    // identity checks still reject a newly assigned mission before reset.
    const token = status.missionToken || requestId()
    const reset = await requestMissionReply(plane, 'resetQuestState', 'missionResetReply', { missionToken: token }, payload =>
      dispatchDeliveryCommand({ shop_id: plane.shop_id, plane_id: plane.id, command: 'resetQuestState', ...payload }))
    if (!reset.accepted) throw new Error(reset.reason || '飞机拒绝复位')
    await Promise.allSettled(['fetchPaidOrderList', 'fetchSiteList'].map(action => store.dispatch(action)))
  } finally {
    resetInFlight.delete(key)
  }
}
export async function executeConfirmedMission (plane, order, isCurrent, resume = false) {
  if (!plane || (!resume && !order)) throw new Error('未找到当前飞机的订单任务')
  const params = { shop_id: plane.shop_id, plane_id: plane.id }
  if (!resume) params.order_id = order.id
  // Resume can target either an order allocation or an independently uploaded route.
  let task
  if (resume) {
    try {
      task = await getDeliveryTask(params)
      params.order_id = task.order_id
    } catch (error) {
      task = await getStandaloneRoute(params)
    }
  } else task = await getDeliveryTask(params)
  if (!isCurrent() || task.plane_code !== plane.plane_code) throw new Error('所选飞机已变化，请重新操作')
  if (!resume && (!order.mission_token || order.mission_token !== task.mission_token)) throw new Error('任务已变化，请重新操作')
  const reply = await requestMissionReply(plane, 'missionStatusRequest', 'missionHandshake')
  if (!isCurrent()) throw new Error('所选飞机已变化，请重新操作')
  const identityHint = resume && !params.order_id
    ? '飞机当前航线与后端记录不一致，可能已重启或航点未写入；请加锁后重新拖拽上传航线，再解锁复航'
    : '飞机当前航线与订单任务不一致，可能已重启或航点未写入；请加锁后重新准备任务'
  if (typeof reply.missionToken === 'string' && reply.missionToken !== task.mission_token) {
    const error = new Error(identityHint)
    // Only a live preflight mismatch may release the order, never resume or in-flight checks.
    if (!resume && reply.accepted === true && [1, 4].includes(Number(reply.questState))) error.code = 'MISSION_TOKEN_MISMATCH'
    throw error
  }
  if (!reply.ready || reply.missionToken !== task.mission_token) throw new Error(identityHint)
  if (!reply.armed) throw new Error('飞机尚未解锁')
  if (!resume && Number(reply.questState) !== 4 && Number(reply.questState) !== 8) throw new Error('飞机当前不允许执行任务')
  const latest = resume && !params.order_id ? await getStandaloneRoute(params) : await getDeliveryTask(params)
  if (!isCurrent() || latest.mission_token !== task.mission_token || latest.plane_code !== plane.plane_code) throw new Error('任务已变化，请重新操作')
  const command = resume ? 'autoMode' : 'setQuestState'
  const result = await requestMissionReply(plane, command, 'missionExecutionReply', {
    state: 8, missionToken: task.mission_token, handshakeId: reply.requestId
  }, payload => {
    if (!isCurrent()) throw new Error('任务已变化，请重新操作')
    return dispatchDeliveryCommand({ ...params, command, ...payload })
  })
  if (!result.accepted) throw new Error(result.reason || '飞机拒绝执行任务')
  return result
}
